// src/components/UpdateClassModal/UpdateClassModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Form, Input, InputNumber, Select, Upload, Button, message, Spin
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import axiosInstance from '../../Config/axiosConfig';

const { TextArea } = Input;
const { Option } = Select;

function UpdateClassModal({ visible, onCancel, classInfo, onUpdateSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchingLookups, setFetchingLookups] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [educationClasses, setEducationClasses] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);

  const findIdByName = (arr, name) => {
    if (!arr || !name) return null;
    const item = arr.find((i) => i.name === name);
    return item?.id || null;
  };

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        setFetchingLookups(true);
        const res = await axiosInstance.post("/Public/get-lookups");
        const data = res.data.data;

        setSubjects(data.subjects || []);
        setEducationClasses(data.educationClasses || []);

        const storedLevels = JSON.parse(localStorage.getItem("InstructorEducationLevel") || '[]');
        const filteredLevels = data.educationLevels?.filter(l => storedLevels.includes(l.id));
        setEducationLevels(filteredLevels?.length ? filteredLevels : data.educationLevels || []);
      } catch (err) {
        console.error("Error loading lookups", err);
        message.error("Failed to load lookup data");
      } finally {
        setFetchingLookups(false);
      }
    };

    if (visible) {
      fetchLookups();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && classInfo && !fetchingLookups) {
      form.setFieldsValue({
        title: classInfo.title || '',
        description: classInfo.description || '',
        resource: classInfo.resource || '',
        price: classInfo.cost || classInfo.price || 0,
        duration: classInfo.duration ? Math.floor(classInfo.duration / 60) : 0,
        subject_id: classInfo.subject_id || findIdByName(subjects, classInfo.subject),
        education_level_id: classInfo.education_level_id || classInfo.educationLevel?.id || findIdByName(educationLevels, classInfo.education_level),
        education_class_id: classInfo.education_class_id || classInfo.educationClass?.id || findIdByName(educationClasses, classInfo.education_class),
      });
    }
  }, [visible, classInfo, fetchingLookups, subjects, educationLevels, educationClasses]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const token = localStorage.getItem("accessToken");
      const deviceId = localStorage.getItem("deviceId");
      const userIp = localStorage.getItem("userIp");

      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("resource", values.resource);
      formData.append("education_level_id", values.education_level_id);
      formData.append("education_class_id", values.education_class_id);
      formData.append("subject_id", values.subject_id);
      formData.append("price", values.price);
      if (classInfo?.id || classInfo?.uid) {
        formData.append("uid", classInfo.id || classInfo.uid);
      }

      projectFiles.forEach((file) => {
        formData.append("resourceFile", file.originFileObj);
      });

      const res = await axiosInstance.post("/Class/update-class-info", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          UserDevice: deviceId,
          UserIPAddress: userIp,
        },
      });

      if (res.data.code === 200) {
        message.success("Class updated successfully!");
        onUpdateSuccess();
        onCancel();
      } else {
        message.error(res.data.message || "Failed to update class");
      }
    } catch (error) {
      message.error(error.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const educationClassOptions = useMemo(() => {
    const selectedLevel = form.getFieldValue("education_level_id");
    return educationClasses
      .filter(cls => cls.education_level_id === selectedLevel)
      .map(({ id, name }) => (
        <Option key={id} value={id}>{name}</Option>
      ));
  }, [form.getFieldValue("education_level_id"), educationClasses]);

  return (
    <Modal
      title="Update Class"
      visible={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={700}
    >
      {fetchingLookups ? (
        <Spin size="large" />
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="resource" label="Project Description">
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="price" label="Price">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Upload Files">
            <Upload
              beforeUpload={() => false}
              onChange={({ fileList }) => setProjectFiles(fileList)}
              multiple
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="education_level_id" label="Education Level">
            <Select>{educationLevels.map(({ id, name }) => <Option key={id} value={id}>{name}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="education_class_id" label="Education Class">
            <Select>{educationClassOptions}</Select>
          </Form.Item>
          <Form.Item name="subject_id" label="Subject">
            <Select>{subjects.map(({ id, name }) => <Option key={id} value={id}>{name}</Option>)}</Select>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

export default UpdateClassModal;
